import axios from 'axios';

// Helper function to get the base URL with fallbacks
const getBaseURL = () => {
  // Check for window.env first (runtime injection)
  const windowEnv = typeof window !== 'undefined' ? window.env || {} : {};
  const configuredUrl = windowEnv.REACT_APP_API_BASE_URL;
  
  // If window.env has a valid URL, use it
  if (configuredUrl) {
    // Check if the URL is just '/api' and replace it with the full domain
    if (configuredUrl === '/api') {
      console.log('Setting base URL to https://api.weedhurry.com/api');
      return 'https://api.weedhurry.com/api';
    }
    console.log('Using API URL from window.env:', configuredUrl);
    return configuredUrl;
  }
  
  // Otherwise fall back to process.env (build-time injection)
  if (process.env.REACT_APP_API_BASE_URL) {
    // Also check for relative path in process.env
    if (process.env.REACT_APP_API_BASE_URL === '/api') {
      console.log('Setting base URL to https://api.weedhurry.com/api');
      return 'https://api.weedhurry.com/api';
    }
    console.log('Using API URL from process.env:', process.env.REACT_APP_API_BASE_URL);
    return process.env.REACT_APP_API_BASE_URL;
  }
  
  // Final fallback based on environment
  const isDevelopment = process.env.NODE_ENV === 'development';
  // Include '/api' in the fallback URL for production
  const fallbackURL = isDevelopment ? 'http://localhost:5001' : 'https://api.weedhurry.com/api';
  console.log('Using fallback API URL:', fallbackURL);
  return fallbackURL;
};

// Get current environment
const isDevelopment = process.env.NODE_ENV === 'development';

// Create the API instance with the determined base URL
const api = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true, // Always include credentials
  timeout: 30000, // Extended timeout to 30 seconds
  headers: {
    'Content-Type': 'application/json'
  }
});

console.log('Axios Base URL:', api.defaults.baseURL);

// Helper function to get cookie by name
const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// Helper function to log cookies for debugging (only names, not values for security)
const logAvailableCookies = () => {
  if (typeof document === 'undefined') return;
  
  const cookies = document.cookie.split(';').map(c => c.trim());
  const cookieNames = cookies.map(cookie => {
    const parts = cookie.split('=');
    return parts.length > 0 ? parts[0] : '';
  }).filter(Boolean);
  
  console.log('Available cookies:', cookieNames.join(', '));
};

// Add request interceptor for authentication and logging
api.interceptors.request.use(
  (config) => {
    // Add timestamp to prevent caching
    config.params = {
      ...config.params,
      _t: Date.now()
    };
    
    // Make sure withCredentials is always set
    config.withCredentials = true;
    
    // Get token from multiple sources with priority:
    // 1. Cookie (if available and readable)
    // 2. localStorage (as backup)
    let accessToken = getCookie('accessToken');
    
    // If no cookie token, try localStorage
    if (!accessToken) {
      accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        console.log('Using token from localStorage');
      }
    }
    
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
      console.log(`Adding auth token to request: ${config.method?.toUpperCase()} ${config.url}`);
    } else {
      console.log(`No auth token found for request: ${config.method?.toUpperCase()} ${config.url}`);
      // Debug cookie availability
      logAvailableCookies();
    }
    
    // Debug logging to help troubleshoot URL construction
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Interceptor to handle 401 errors and refresh tokens
api.interceptors.response.use(
  (response) => {
    console.log(`API Response Success: ${response.config.method?.toUpperCase()} ${response.config.url}`);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    if (!error.response) {
      console.error('Network Error - likely CORS or server connectivity issue:', error.message);
    } else {
      console.error(`API Response Error: ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`, {
        status: error.response?.status,
        message: error.message,
        data: error.response?.data
      });
    }

    // Don't retry if it's a logout request or already retried
    if (originalRequest?.url?.includes('/auth/logout') || originalRequest?._retry) {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      console.log('Authentication error detected, attempting token refresh...');
      originalRequest._retry = true;
      
      try {
        // Determine the correct refresh token URL
        const baseURL = api.defaults.baseURL;
        const refreshURL = `${baseURL}/auth/refresh-token`;
        
        console.log('Attempting token refresh at:', refreshURL);
        
        // Make the refresh token request with explicit credentials
        const refreshResponse = await axios.post(
          refreshURL,
          {},
          { 
            withCredentials: true,
            headers: { 
              'Content-Type': 'application/json'
            }
          }
        );

        console.log('Token refresh response:', refreshResponse.data);

        if (refreshResponse.data?.success) {
          console.log('Token refresh successful, retrying original request');
          
          // Save new token to localStorage for redundancy
          if (refreshResponse.data.accessToken) {
            localStorage.setItem('accessToken', refreshResponse.data.accessToken);
            originalRequest.headers['Authorization'] = `Bearer ${refreshResponse.data.accessToken}`;
          }
          
          return api(originalRequest);
        } else {
          console.log('Token refresh failed with unsuccessful response');
          handleAuthFailure();
        }
      } catch (refreshError) {
        console.error('Token refresh error:', refreshError);
        handleAuthFailure();
        return Promise.reject(refreshError);
      }
    }

    // Handle other common errors
    if (error.response?.status === 503) {
      console.error('Service temporarily unavailable');
    }

    if (error.response?.status === 404) {
      console.error('Resource not found:', error.config?.url);
    }

    return Promise.reject(error);
  }
);

// Enhanced helper function to handle authentication failures
const handleAuthFailure = () => {
  console.log('Handling authentication failure');
  
  // Include vendor menu URLs in public routes
  const publicRoutes = [
    '/', 
    '/nearby', 
    '/login', 
    '/register', 
    '/add-strain', 
    '/terms', 
    '/contact'
  ];
  
  // Check if current path is a vendor menu page
  const currentPath = window.location.pathname;
  const isVendorMenuPage = currentPath.includes('/vendor/') && currentPath.includes('/menu');
  
  // Get the domain dynamically
  const domain = isDevelopment ? 'localhost' : '.weedhurry.com';
  
  // Clear cookies with environment-aware domain
  const cookieOptions = `Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; ${
    !isDevelopment ? `Domain=${domain};` : ''
  } ${!isDevelopment ? 'Secure; SameSite=None' : ''}`;
  
  document.cookie = `accessToken=; ${cookieOptions}`;
  document.cookie = `refreshToken=; ${cookieOptions}`;
  
  // Clear localStorage
  localStorage.removeItem('auth');
  localStorage.removeItem('accessToken');
  sessionStorage.clear();

  // Only redirect to login if not on a public route and not a vendor menu page
  if (!publicRoutes.includes(currentPath) && !isVendorMenuPage) {
    console.log('Redirecting to login page');
    window.location.href = '/login';
  } else {
    console.log('On public route or vendor menu page, not redirecting');
  }
};

// Enhance the api object with additional methods
api.getCookie = getCookie;
api.handleAuthFailure = handleAuthFailure;

// Add method to check auth status
api.checkAuth = async () => {
  try {
    const response = await api.get('/auth/validate', { withCredentials: true });
    return response.data?.success === true;
  } catch (error) {
    return false;
  }
};

// Export the enhanced api object
export default api;